import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../common/Button';
import { Input, Select } from '../common/FormFields';
import { sendVerificationEmail } from '../../services/auth/emailVerification';
import { logError } from '../../utils/logger';

export const TrialSubscriptionFlow = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    company: '',
    role: '',
    customRole: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validations, setValidations] = useState({});

  const ROLE_OPTIONS = [
    { value: 'company_owner', label: 'Company Owner' },
    { value: 'manager', label: 'Manager' },
    { value: 'driver', label: 'Driver' },
    { value: 'permit_agent', label: 'Permit Agent' },
    { value: 'dispatch', label: 'Dispatch' },
    { value: 'other', label: 'Other' }
  ];

  const handleValidation = (field, result) => {
    setValidations(prev => ({
      ...prev,
      [field]: result
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Check if all validations pass
      const allValid = Object.values(validations).every(v => v.isValid);
      if (!allValid) {
        throw new Error('Please fix validation errors before continuing');
      }

      // Prepare form data with final role value
      const finalFormData = {
        ...formData,
        role: formData.role === 'other' ? formData.customRole : formData.role
      };

      localStorage.setItem('signupFormData', JSON.stringify(finalFormData));
      localStorage.setItem('signupType', 'beta');
      localStorage.setItem('pendingEmail', formData.email);

      const emailSent = await sendVerificationEmail(formData.email);
      if (!emailSent) {
        throw new Error('Failed to send verification email');
      }

      navigate('/verify');
    } catch (err) {
      logError('Trial signup failed:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#3D3F42] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-[#2A2B2E] rounded-lg p-8">
          <h2 className="text-2xl font-bold text-white mb-6">Beta Tester Signup</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
                {error}
              </div>
            )}

            <Input
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              onValidation={(result) => handleValidation('email', result)}
              validate={true}
              required
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />

            <Input
              label="Password"
              type="password"
              name="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              onValidation={(result) => handleValidation('password', result)}
              validate={true}
              required
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />

            <Input
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />

            <Input
              label="Phone Number"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />

            <Input
              label="Company Name"
              name="company"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              required
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />

            <Select
              label="Role"
              value={formData.role}
              onChange={(value) => setFormData({ ...formData, role: value })}
              options={ROLE_OPTIONS}
              required
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />

            {formData.role === 'other' && (
              <Input
                label="Specify Role"
                name="customRole"
                value={formData.customRole}
                onChange={(e) => setFormData({ ...formData, customRole: e.target.value })}
                placeholder="Enter your role"
                required
                className="bg-gray-700 border-gray-600 text-white"
                labelClassName="text-gray-300"
              />
            )}

            <Button
              type="submit"
              loading={loading}
              disabled={loading || !Object.values(validations).every(v => v.isValid)}
              className="w-full bg-red-600 hover:bg-red-700 text-white"
            >
              Join Beta Program
            </Button>
          </form>

          <p className="text-center text-gray-400 mt-4">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-red-400 hover:text-red-300"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};